import React from 'react';
import { Formik, Form } from 'formik';
import { Button } from '@/components';
import BottomModal from '@/components/BottomModal';
import Input from '@/components/Input';

interface AddressData {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  notes: string;
}

interface AddressFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (address: string) => void;
  prompt?: string;
  disabled?: boolean;
  primaryColor?: string;
  containedModal?: boolean;
}

const initialValues: AddressData = {
  street: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  notes: ''
};

const AddressFormModal: React.FC<AddressFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  disabled = false,
  primaryColor,
  containedModal = false
}) => {
  const validate = (values: AddressData) => {
    const errors: Partial<AddressData> = {};
    if (!values.street.trim()) errors.street = 'Street address is required';
    if (!values.city.trim()) errors.city = 'City is required';
    if (!values.state.trim()) errors.state = 'State/Province is required';
    if (!values.postalCode.trim()) errors.postalCode = 'Postal code is required';
    if (!values.country.trim()) errors.country = 'Country is required';
    return errors;
  };

  const handleSubmit = async (
    values: AddressData,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    const addressParts = [
      values.street,
      values.city,
      values.state,
      values.postalCode,
      values.country
    ];
    if (values.notes.trim()) addressParts.push(`(${values.notes})`);

    try {
      await onSubmit(addressParts.join(', '));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reusable form fields (no buttons) ──────────────────────────────────────
  const renderFields = (
    values: AddressData,
    errors: Partial<AddressData>,
    touched: Partial<Record<keyof AddressData, boolean>>,
    handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
    handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>,
    isSubmitting: boolean
  ) => (
    <div className="p-4 space-y-4">
      <Input
        name="street"
        type="text"
        label={<>Street Address <span className="text-red-500">*</span></>}
        placeholder="123 Main St, Apt 4B"
        value={values.street}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled || isSubmitting}
        error={touched.street && !!errors.street}
        helperText={touched.street ? errors.street : ''}
      />
      <Input
        name="city"
        type="text"
        label={<>City <span className="text-red-500">*</span></>}
        placeholder="New York"
        value={values.city}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled || isSubmitting}
        error={touched.city && !!errors.city}
        helperText={touched.city ? errors.city : ''}
      />
      <Input
        name="state"
        type="text"
        label={<>State/Province <span className="text-red-500">*</span></>}
        placeholder="NY"
        value={values.state}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled || isSubmitting}
        error={touched.state && !!errors.state}
        helperText={touched.state ? errors.state : ''}
      />
      <Input
        name="postalCode"
        type="text"
        label={<>Postal Code <span className="text-red-500">*</span></>}
        placeholder="10001"
        value={values.postalCode}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled || isSubmitting}
        error={touched.postalCode && !!errors.postalCode}
        helperText={touched.postalCode ? errors.postalCode : ''}
      />
      <Input
        name="country"
        type="text"
        label={<>Country <span className="text-red-500">*</span></>}
        placeholder="United States"
        value={values.country}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled || isSubmitting}
        error={touched.country && !!errors.country}
        helperText={touched.country ? errors.country : ''}
      />
      <div className="w-full">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Additional Notes (Optional)
        </label>
        <textarea
          name="notes"
          value={values.notes}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Delivery instructions, apartment number, etc."
          rows={2}
          disabled={disabled || isSubmitting}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary focus:border-primary disabled:bg-gray-100 dark:disabled:bg-gray-700  disabled:cursor-not-allowed transition-colors dark:bg-gray-700 dark:text-white dark:border-gray-600"
        />
      </div>
    </div>
  );

  // ── Reusable footer buttons ─────────────────────────────────────────────────
  const renderFooter = (isValid: boolean, isSubmitting: boolean) => (
    <div className="flex gap-3 px-4 py-3">
      <Button
        type="button"
        variant="outline"
        onClick={onClose}
        disabled={isSubmitting}
        className="flex-1"
      >
        Cancel
      </Button>
      <button
        type="submit"
        disabled={!isValid || isSubmitting}
        className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-100 transition-all duration-200 shadow-sm hover:shadow-md"
        style={{ backgroundColor: primaryColor }}
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </button>
    </div>
  );

  return (
    <>
      {containedModal ? (
        // ── CONTAINED MODAL (used inside chat widget) ───────────────────────
        // Layout: fixed header → scrollable body → fixed footer
        <div
          className={`absolute inset-0 z-50 flex items-end justify-center overflow-hidden ${isOpen ? 'block' : 'hidden'
            }`}
        >
          <div className="w-full bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col max-h-[60vh] animate-in slide-in-from-bottom duration-300">

            {/* ── FIXED HEADER ── */}
            <div className="shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Please provide your address
              </h2>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Formik wraps BODY + FOOTER so submit works */}
            <Formik
              initialValues={initialValues}
              validate={validate}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ errors, touched, values, handleChange, handleBlur, isValid, isSubmitting }) => (
                <Form className="flex flex-col flex-1 min-h-0">

                  {/* ── SCROLLABLE MIDDLE ── */}
                  <div className="flex-1 overflow-y-auto">
                    {renderFields(values, errors, touched, handleChange, handleBlur, isSubmitting)}
                  </div>

                  {/* ── FIXED FOOTER ── */}
                  <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                    {renderFooter(isValid, isSubmitting)}
                  </div>

                </Form>
              )}
            </Formik>
          </div>
        </div>
      ) : (
        // ── BOTTOM MODAL (full-screen use) ─────────────────────────────────
        // Formik lives here so both customContent and footer share the same form context.
        // Fields → customContent (goes into BottomModal's scrollable zone)
        // Buttons → footer prop  (goes into BottomModal's fixed footer slot)
        <Formik
          initialValues={initialValues}
          validate={validate}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, values, handleChange, handleBlur, isValid, isSubmitting }) => (
            <Form>
              <BottomModal
                isOpen={isOpen}
                onClose={onClose}
                title="Please provide your address"
                sections={[]}
                selectedItem={null}
                onItemSelect={() => { }}
                showSearch={false}
                customContent={
                  renderFields(values, errors, touched, handleChange, handleBlur, isSubmitting)
                }
                footer={renderFooter(isValid, isSubmitting)}
              />
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default AddressFormModal;
